import React from 'react';
import { Checkbox, Tooltip } from '@cambridgeassessment/cambridge-ui';
import {
  StyledFileTypes,
  StyledFileTypeContainer,
  StyledFileNameContainer,
  StyledWordDocumentContainer,
  StyledPlagiarismContainer,
  StyledPlagiarismDescription,
} from './styles';
import { FileType } from './fileTypes';
import { FileTypeName, Roles } from '@cambridgeassessment/lib-ee-commons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { displayNotification } from '../../../../../store/notification/actions';
import { LocalSection, Section } from '../../../../../service/components/types';
import thunks from '../../../../../store/components/thunks';
import { StyledReadOnlyComponent } from '../styles';
import { Authorized } from 'lib-browser';

interface Props {
  fileType: FileType;
  disabled?: boolean;
  section: Section | LocalSection;
  isTheLastChecked: boolean;
  onChange: (name: string, value: boolean) => void;
  dataQaId: string;
  isDocumentChecked?: boolean;
  isWordDocumentChecked?: boolean;
  componentId: string;
  componentName: string;
  showSnackbar: (isTheLastChecked: boolean, fileType?: FileTypeName, isWordDocTheLastOneStanding?: boolean) => void;
  onlyWordDocChecked: boolean;
}

const DocumentFileType = ({
  fileType,
  section,
  disabled,
  onChange,
  isTheLastChecked,
  dataQaId,
  isDocumentChecked,
  isWordDocumentChecked,
  componentId,
  componentName,
  showSnackbar,
  onlyWordDocChecked,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const userSelectedRole = useSelector((state: RootState) => state.userData.selectedRole);
  const isPermittedSetPlagiarism = [
    Roles.SUBMIT_SUPER_ADMIN,
    Roles.SUBMIT_COMPLIANCE,
    Roles.SUBMIT_ADMINISTRATOR,
  ].includes(userSelectedRole?.name);
  const document = FileTypeName.DOCUMENT;
  const wordDocument = FileTypeName.WORD_DOCUMENT_ONLY;
  const isPlagiarismChecked = !!section.plagiarismAndCollusion;
  const notOnlyDocumentChecked = section.fileTypes?.some((item: FileType) => item.name !== FileTypeName.DOCUMENT);
  const notOnlyWordDocumentChecked = section.fileTypes?.some(
    (item: FileType) => item.name !== FileTypeName.WORD_DOCUMENT_ONLY,
  );

  const documentCheckboxTicked = isDocumentChecked ? notOnlyDocumentChecked : notOnlyWordDocumentChecked;

  const checkboxDisabled = disabled || isPlagiarismChecked;

  const formattedFiles = fileType.formats.map((str: string): string => str.substring(1)).join(' ');

  const onClick = (fileName: FileTypeName): void => showSnackbar(isTheLastChecked, fileName, onlyWordDocChecked);

  const onPlagiarismAndCollusionChange = (): void => {
    const sectionToUpdate = { ...section, plagiarismAndCollusion: !section.plagiarismAndCollusion } as Section;
    dispatch(thunks.updatePlagiarismFlag(componentId, sectionToUpdate, 'plagiarismAndCollusion', sectionToUpdate.id));
  };

  const showSnackbarForPlagiarismCheckbox = (): void => {
    if (!disabled) {
      if (!isPermittedSetPlagiarism) {
        dispatch(
          displayNotification(
            'In order for this action to be enabled, only either ‘Document’ or ‘Word Document Only’ type must be selected, but only the Compliance Officer role can edit this section.',
          ),
        );
      }

      if (documentCheckboxTicked && isPermittedSetPlagiarism) {
        dispatch(
          displayNotification(
            'In order for this action to be enabled, only either ‘Document’ or ‘Word Document Only’ type must be selected.',
          ),
        );
      }
    }
  };

  const PlagiarismCollusion = (): React.ReactElement => {
    const label = 'Perform plagiarism and collusion checks for document or word document only types';
    return (
      <StyledPlagiarismContainer>
        <div onClick={showSnackbarForPlagiarismCheckbox}>
          <Checkbox
            checked={isPlagiarismChecked}
            disabled={documentCheckboxTicked || disabled || !isPermittedSetPlagiarism}
            onChange={onPlagiarismAndCollusionChange}
            dataQaId={`${componentName}-${section.name}-plagiarism-checkbox`}
            ariaLabel={label}
          />
        </div>
        <Tooltip
          arrow={true}
          placement='top-start'
          dataQaId='plagiarism-and-collusion-description'
          title='If enabled, then plagiarism and collusion checks will be performed on component submission for the document or word document only file type.'
          enterDelay={500}
        >
          <StyledPlagiarismDescription>{label}</StyledPlagiarismDescription>
        </Tooltip>
      </StyledPlagiarismContainer>
    );
  };

  const WordDocumentFileType = (props?: { readOnly?: boolean }): React.ReactElement => (
    <StyledReadOnlyComponent data-qa-id={`read-only-wrapper-${props?.readOnly}`} readOnly={props?.readOnly}>
      <StyledWordDocumentContainer>
        <div onClick={(): void => onClick(wordDocument)}>
          <Checkbox
            dataQaId={`${dataQaId}-checkbox-${wordDocument}`}
            checked={isWordDocumentChecked}
            onChange={(value: boolean): void => {
              onChange(wordDocument, value);
            }}
            disabled={checkboxDisabled || onlyWordDocChecked}
            ariaLabel={wordDocument}
          />
        </div>
        <StyledFileNameContainer>
          <div>{wordDocument}</div>
          <StyledFileTypes>docx</StyledFileTypes>
        </StyledFileNameContainer>
      </StyledWordDocumentContainer>
    </StyledReadOnlyComponent>
  );

  const RenderComponent = (props?: { readOnly?: boolean }): React.ReactElement => (
    <StyledFileTypeContainer>
      <StyledReadOnlyComponent data-qa-id={`read-only-wrapper-${props?.readOnly}`} readOnly={props?.readOnly}>
        <div onClick={(): void => onClick(document)}>
          <Checkbox
            dataQaId={`${dataQaId}-checkbox-${fileType.name}`}
            checked={isDocumentChecked}
            onChange={(value: boolean): void => {
              onChange(fileType.name, value);
            }}
            disabled={checkboxDisabled || isTheLastChecked}
            ariaLabel={fileType.name}
          />
        </div>
      </StyledReadOnlyComponent>
      <StyledFileNameContainer>
        <StyledReadOnlyComponent data-qa-id={`read-only-wrapper-${props?.readOnly}`} readOnly={props?.readOnly}>
          <div>{fileType.name}</div>
          <StyledFileTypes>{formattedFiles}</StyledFileTypes>
        </StyledReadOnlyComponent>
        <Authorized
          selectedRole={userSelectedRole}
          roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR]}
          readOnlyRoles={[Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER]}
        >
          <WordDocumentFileType />
        </Authorized>
        <PlagiarismCollusion />
      </StyledFileNameContainer>
    </StyledFileTypeContainer>
  );

  return (
    <Authorized
      selectedRole={userSelectedRole}
      roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR]}
      readOnlyRoles={[Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER]}
    >
      <RenderComponent />
    </Authorized>
  );
};

export default DocumentFileType;
